import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import Modal, { ModalHeader, ModalBody, ModalFooter } from 'common/components/modal/Modal';
import { Header } from 'common/components/texts/Texts';
import { TextInputController } from 'common/components/textInput/TextInput';
import { SelectInputController, SelectInputOption } from 'common/components/selectInput/SelectInput';
import Button from 'common/components/button/Button';
import Loading from 'common/services/Loading';
import Toast from 'common/services/Toast';
import Logger from 'common/services/Logger';
import { LOGGER_LOG_TYPE } from 'Config';
import FamiliesService from 'api/families/FamiliesService';
import { FamilyDto } from 'api/families/models/FamilyDto';
import BudgetComponentsService from 'api/budgetComponents/BudgetComponentsService';
import { BudgetLineDto } from 'api/budgets/models/BudgetDto';
import { BudgetComponentDto } from 'api/budgets/models/BudgetComponentDto';
import store from 'store/store';

type SaveMode = 'choose' | 'create' | 'update';

interface Props {
    isOpen: boolean;
    onClose: (savedComponentId?: string) => void;
    line: BudgetLineDto;
}

interface FormModel {
    description: string;
    familyId: string | null;
    subFamilyId: string | null;
}

const SaveComponentModal: React.FC<Props> = ({ isOpen, onClose, line }) => {
    const { t } = useTranslation();
    const form = useForm<FormModel>({
        defaultValues: {
            description: line.designation || '',
            familyId: null,
            subFamilyId: null,
        }
    });
    const [families, setFamilies] = useState<FamilyDto[]>([]);
    const [existingComponent, setExistingComponent] = useState<BudgetComponentDto | null>(null);
    const hasLinkedComponent = Boolean(line.sourceBudgetComponentId);

    const [mode, setMode] = useState<SaveMode>(hasLinkedComponent ? 'choose' : 'create');

    const loadData = async () => {
        try {
            const familiesData = await FamiliesService.getElementsCatalog({ familyIds: [], isElement: false, isComponent: true });
            setFamilies(familiesData);

            if (line.sourceBudgetComponentId) {
                const component = await BudgetComponentsService.getById(line.sourceBudgetComponentId);
                setExistingComponent(component);
            }
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t load data', error);
        }
    };

    useEffect(() => {
        if (isOpen) {
            setMode(hasLinkedComponent ? 'choose' : 'create');
            setExistingComponent(null);
            form.reset({
                description: line.designation || '',
                familyId: null,
                subFamilyId: null,
            });
            void loadData();
        }
    }, [isOpen]);

    useEffect(() => {
        if (mode === 'create') {
            form.reset({
                description: line.designation || '',
                familyId: null,
                subFamilyId: null,
            });
        }
    }, [mode]);

    const familiesOptions: SelectInputOption[] = families
        .filter(x => !x.parentId)
        .map(f => ({ value: f.id, label: f.name }));

    const selectedFamilyId = form.watch('familyId');
    const selectedFamily = families.find(x => x.id === selectedFamilyId);
    const subFamiliesOptions: SelectInputOption[] = (selectedFamily?.subFamilies ?? [])
        .map(f => ({ value: f.id, label: f.name }));

    const collectComponentLines = (): BudgetLineDto[] => {
        const lines = store.getState().budget.lines;
        const collectLines = (parentKey: string): BudgetLineDto[] => {
            const children = lines.filter((l: BudgetLineDto) => l.parentKey === parentKey);
            const result: BudgetLineDto[] = [];
            for (const child of children) {
                result.push(child);
                result.push(...collectLines(child.key));
            }
            return result;
        };
        return [line, ...collectLines(line.key)];
    };

    const onSubmitCreate = async (model: FormModel) => {
        try {
            Loading.show();

            const componentLines = collectComponentLines();
            const newId = await BudgetComponentsService.create({
                id: '',
                description: model.description,
                familyId: model.familyId,
                subFamilyId: model.subFamilyId,
                version: '',
                lines: componentLines,
                decimalPlaces: 2,
            });

            Loading.hide();
            Toast.success(t('messages.record_save_success'));
            onClose(newId);
        } catch (error: any) {
            Loading.hide();
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t save component', error);
            Toast.error(t('messages.record_save_error'));
        }
    };

    const onSubmitUpdate = async () => {
        if (!existingComponent) return;

        try {
            Loading.show();

            const componentLines = collectComponentLines();
            await BudgetComponentsService.update({
                ...existingComponent,
                lines: componentLines,
            });

            Loading.hide();
            Toast.success(t('messages.record_save_success'));
            onClose(line.sourceBudgetComponentId!);
        } catch (error: any) {
            Loading.hide();
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t update component', error);
            Toast.error(t('messages.record_save_error'));
        }
    };

    // Step 1: Choose mode (only when linked to existing component)
    if (mode === 'choose') {
        return (
            <Modal isOpen={isOpen} onRequestClose={() => onClose()} style={{ content: { minWidth: 500 } }}>
                <div>
                    <ModalHeader>
                        <Header>{t('budgets.budget.lines.save_component.title')}</Header>
                    </ModalHeader>
                    <ModalBody>
                        <p style={{ marginBottom: '0.5rem' }}>
                            {t('budgets.budget.lines.save_component.linked_message_1')}
                        </p>
                        <p style={{ marginBottom: '1rem' }}>
                            {t('budgets.budget.lines.save_component.linked_message_2')}
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            <Button
                                variant='primary'
                                fw
                                onClick={() => { onSubmitUpdate(); }}
                                disabled={!existingComponent}
                            >
                                {t('budgets.budget.lines.save_component.update_existing')}
                            </Button>
                            <Button
                                variant='secondary'
                                fw
                                onClick={() => setMode('create')}
                            >
                                {t('budgets.budget.lines.save_component.create_new')}
                            </Button>
                            <Button
                                variant='empty'
                                fw
                                onClick={() => onClose()}
                            >
                                {t('common.cancel')}
                            </Button>
                        </div>
                    </ModalBody>
                </div>
            </Modal>
        );
    }

    // Step 2: Form (create or update)
    return (
        <Modal isOpen={isOpen} onRequestClose={() => onClose()} style={{ content: { minWidth: 500 } }}>
            <div>
                <ModalHeader>
                    <Header>{t('budgets.budget.lines.save_component.title')}</Header>
                </ModalHeader>
                <ModalBody>
                    <form onSubmit={form.handleSubmit(onSubmitCreate)}>
                        <Row className="mb-3">
                            <Col xs={12}>
                                <label className="mb-1">{t('budgets.budget.lines.save_component.description')}*</label>
                                <TextInputController
                                    name='description'
                                    control={form.control}
                                    rules={{ required: true }}
                                    placeholder={t('budgets.budget.lines.save_component.description')}
                                />
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col xs={12} sm={6}>
                                <label className="mb-1">{t('elements.family')}</label>
                                <SelectInputController
                                    name='familyId'
                                    control={form.control as any}
                                    options={familiesOptions}
                                    onChange={() => form.setValue('subFamilyId', null)}
                                />
                            </Col>
                            <Col xs={12} sm={6}>
                                <label className="mb-1">{t('elements.sub_family')}</label>
                                <SelectInputController
                                    name='subFamilyId'
                                    control={form.control as any}
                                    options={subFamiliesOptions}
                                    disabled={!selectedFamilyId}
                                />
                            </Col>
                        </Row>
                    </form>
                </ModalBody>
                <ModalFooter className="text-right">
                    {hasLinkedComponent && (
                        <Button variant='empty' onClick={() => setMode('choose')} style={{ marginRight: 'auto' }}>
                            {t('common.go_back')}
                        </Button>
                    )}
                    <Button variant='secondary' onClick={() => onClose()} style={{ marginRight: '0.5rem' }}>
                        {t('common.cancel')}
                    </Button>
                    <Button onClick={form.handleSubmit(onSubmitCreate)}>
                        {t('common.save')}
                    </Button>
                </ModalFooter>
            </div>
        </Modal>
    );
};

export default SaveComponentModal;
